import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import api, { formatApiError } from "../lib/api";

const BLOCK_TYPES = {
  hero_dragon: "Hero com Dragão 3D",
  category_strip: "Faixa de categorias",
  product_grid: "Grade de produtos",
  banner: "Banner",
  authenticity_cta: "CTA autenticidade",
  auction_strip: "Leilões ativos",
};

const PARAM_FIELDS = {
  hero_dragon: ["tagline", "title", "subtitle", "cta_label", "cta_link", "bg_image", "dragon_section"],
  category_strip: ["title"],
  product_grid: ["title", "filter", "limit", "dragon_color", "flame_effect"],
  banner: ["banner_index"],
  authenticity_cta: ["title", "text"],
  auction_strip: ["title"],
};

function BannersTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/banners").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await api.put(`/admin/banners/${editing.id}`, editing);
      else await api.post("/admin/banners", editing);
      toast.success("Banner salvo");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-5">
        <Button onClick={() => setEditing({ title: "", subtitle: "", image: "", link: "/", active: true, order: items.length + 1 })}
          className="bg-gold text-black hover:bg-gold-light" data-testid="new-banner-button">
          <Plus size={15} className="mr-1.5" /> Novo banner
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((b) => (
          <div key={b.id} className="relative border border-white/10 rounded-lg overflow-hidden min-h-[140px] flex items-end" data-testid={`banner-card-${b.id}`}>
            {b.image && <img src={b.image} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-black/20" />
            <div className="relative p-5 flex-1 flex items-end justify-between gap-3">
              <div>
                <p className="font-display text-lg">{b.title}</p>
                <p className="text-xs text-zinc-400">{b.subtitle}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" className="border-white/20 text-zinc-200" onClick={() => setEditing({ ...b })} data-testid="edit-banner-button"><Pencil size={13} /></Button>
                <Button size="sm" variant="outline" className="border-white/20 text-zinc-200 hover:text-red-400"
                  onClick={async () => { if (window.confirm("Excluir banner?")) { await api.delete(`/admin/banners/${b.id}`); load(); } }} data-testid="delete-banner-button">
                  <Trash2 size={13} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111111] border-white/10" data-testid="banner-dialog">
          <DialogHeader><DialogTitle className="font-display">Banner</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4">
              {[["Título", "title"], ["Subtítulo", "subtitle"], ["Imagem (URL)", "image"], ["Link", "link"]].map(([label, key]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input value={editing[key] || ""} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })} data-testid={`banner-${key}-input`}
                    className="mt-1.5 bg-[#141414] border-white/10" />
                </div>
              ))}
              <label className="flex items-center gap-2.5 text-sm text-zinc-300">
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="banner-active-switch" /> Ativo
              </label>
              <Button type="submit" className="w-full bg-gold text-black hover:bg-gold-light" data-testid="save-banner-button">Salvar</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PARAM_LABELS = {
  title: "Título", subtitle: "Subtítulo", filter: "Filtro", limit: "Limite",
  tagline: "Chamada (texto pequeno acima do título)",
  dragon_color: "Cor do dragão", cta_label: "Texto do botão", cta_link: "Link do botão",
  bg_image: "Imagem de fundo", dragon_section: "Seção do dragão", banner_index: "Índice do banner", text: "Texto",
  flame_effect: "Efeito de chama no fundo",
};

function BlocksTab() {
  const [blocks, setBlocks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newType, setNewType] = useState("product_grid");
  const load = () => api.get("/admin/blocks").then((r) => setBlocks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const move = async (index, dir) => {
    const ids = blocks.map((b) => b.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    await api.put("/admin/blocks/reorder", { ids });
    load();
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/blocks/${editing.id}`, { params: editing.params, enabled: editing.enabled });
      toast.success("Bloco salvo");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addBlock = async () => {
    await api.post("/admin/blocks", { page: "home", type: newType, params: {} });
    toast.success("Bloco adicionado");
    load();
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Select value={newType} onValueChange={setNewType}>
          <SelectTrigger className="w-64 bg-[#141414] border-white/10" data-testid="new-block-type"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-white/10">
            {Object.entries(BLOCK_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={addBlock} className="bg-gold text-black hover:bg-gold-light" data-testid="add-block-button">
          <Plus size={15} className="mr-1.5" /> Adicionar bloco
        </Button>
      </div>

      <div className="space-y-3" data-testid="blocks-list">
        {blocks.map((b, i) => (
          <div key={b.id} className={`flex items-center gap-4 border rounded-lg p-4 bg-[#111111] ${b.enabled ? "border-white/10" : "border-white/5 opacity-50"}`} data-testid={`block-row-${b.type}`}>
            <div className="flex flex-col gap-1">
              <button onClick={() => move(i, -1)} className="text-zinc-500 hover:text-gold disabled:opacity-30" disabled={i === 0} data-testid="block-move-up" aria-label="Subir"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} className="text-zinc-500 hover:text-gold disabled:opacity-30" disabled={i === blocks.length - 1} data-testid="block-move-down" aria-label="Descer"><ArrowDown size={14} /></button>
            </div>
            <div className="flex-1">
              <p className="text-sm text-zinc-100">{BLOCK_TYPES[b.type] || b.type}</p>
              <p className="text-xs text-zinc-600">{b.params?.title || b.params?.filter || ""}</p>
            </div>
            <Switch checked={!!b.enabled} onCheckedChange={async (v) => { await api.put(`/admin/blocks/${b.id}`, { enabled: v }); load(); }} data-testid="block-enabled-switch" />
            <Button size="sm" variant="outline" className="border-white/15 text-zinc-300 hover:text-gold" onClick={() => setEditing({ ...b, params: { ...b.params } })} data-testid="edit-block-button">
              <Pencil size={13} className="mr-1" /> Parâmetros
            </Button>
            <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400"
              onClick={async () => { if (window.confirm("Excluir bloco?")) { await api.delete(`/admin/blocks/${b.id}`); load(); } }} data-testid="delete-block-button">
              <Trash2 size={13} />
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111111] border-white/10" data-testid="block-dialog">
          <DialogHeader><DialogTitle className="font-display">{BLOCK_TYPES[editing?.type]}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4">
              {(PARAM_FIELDS[editing.type] || []).map((field) => (
                <div key={field}>
                  <Label>{PARAM_LABELS[field] || field}</Label>
                  {field === "filter" ? (
                    <Select value={editing.params.filter || "all"} onValueChange={(v) => setEditing({ ...editing, params: { ...editing.params, filter: v } })}>
                      <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="block-param-filter"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-white/10">
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="featured">Destaques</SelectItem>
                        <SelectItem value="digital">Digitais</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : field === "flame_effect" ? (
                    <Select value={editing.params.flame_effect || "off"} onValueChange={(v) => setEditing({ ...editing, params: { ...editing.params, flame_effect: v } })}>
                      <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="block-param-flame"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-white/10">
                        <SelectItem value="off">Desativado</SelectItem>
                        <SelectItem value="on">Ativado</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : field === "dragon_color" ? (
                    <div className="flex gap-2 items-center mt-1.5">
                      <input type="color" value={editing.params.dragon_color || "#D4AF37"} data-testid="block-param-dragon-color"
                        onChange={(e) => setEditing({ ...editing, params: { ...editing.params, dragon_color: e.target.value } })}
                        className="w-10 h-10 rounded border border-white/10 bg-transparent cursor-pointer shrink-0" />
                      <Input value={editing.params.dragon_color ?? ""} placeholder="#D4AF37 (vazio = cor global)" data-testid="block-param-dragon-color-hex"
                        onChange={(e) => setEditing({ ...editing, params: { ...editing.params, dragon_color: e.target.value } })}
                        className="bg-[#141414] border-white/10 font-mono text-xs" />
                    </div>
                  ) : (
                    <Input value={editing.params[field] ?? ""} data-testid={`block-param-${field}`}
                      onChange={(e) => setEditing({ ...editing, params: { ...editing.params, [field]: field === "limit" || field === "banner_index" ? parseInt(e.target.value) || 0 : e.target.value } })}
                      className="mt-1.5 bg-[#141414] border-white/10" />
                  )}
                </div>
              ))}
              <label className="flex items-center gap-2.5 text-sm text-zinc-300">
                <Switch checked={editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} data-testid="block-dialog-enabled" /> Ativo
              </label>
              <Button type="submit" className="w-full bg-gold text-black hover:bg-gold-light" data-testid="save-block-button">Salvar</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminContent() {
  return (
    <div data-testid="admin-content">
      <h1 className="font-display text-3xl tracking-tight mb-10">Banners & Blocos</h1>
      <Tabs defaultValue="blocks">
        <TabsList className="bg-white/5 mb-8">
          <TabsTrigger value="blocks" className="data-[state=active]:bg-gold data-[state=active]:text-black" data-testid="tab-blocks">Blocos da Home</TabsTrigger>
          <TabsTrigger value="banners" className="data-[state=active]:bg-gold data-[state=active]:text-black" data-testid="tab-banners">Banners</TabsTrigger>
        </TabsList>
        <TabsContent value="blocks"><BlocksTab /></TabsContent>
        <TabsContent value="banners"><BannersTab /></TabsContent>
      </Tabs>
    </div>
  );
}
